import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  onSignatureChange: (dataUrl: string | null) => void
  width?: number
  height?: number
}

interface IPoint {
  x: number
  y: number
}

@observer
export default class SignatureCanvas extends React.Component<IProps> {
  private canvasRef = React.createRef<HTMLCanvasElement>()
  private ctx: CanvasRenderingContext2D | null = null

  @observable private isDrawing = false
  @observable private hasSignature = false
  private strokeHistory: IPoint[][] = []
  private currentStroke: IPoint[] = []

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public componentDidMount() {
    this.initCanvas()
  }

  public componentDidUpdate(prevProps: IProps) {
    if (
      prevProps.width !== this.props.width ||
      prevProps.height !== this.props.height
    ) {
      this.initCanvas()
    }
  }

  public render() {
    const { width = 400, height = 200 } = this.props

    return (
      <div className="signature-canvas-container relative">
        <canvas
          ref={this.canvasRef}
          width={width}
          height={height}
          className="signature-canvas"
          onMouseDown={this.startDrawing}
          onMouseMove={this.draw}
          onMouseUp={this.stopDrawing}
          onMouseLeave={this.stopDrawing}
          onTouchStart={this.handleTouchStart}
          onTouchMove={this.handleTouchMove}
          onTouchEnd={this.stopDrawing}
        />
        {!this.hasSignature && !this.isDrawing && (
          <div className="signature-canvas-placeholder absolute">
            <span>{Localization.translator.signHere}</span>
          </div>
        )}
      </div>
    )
  }

  public clear = () => {
    if (!this.ctx || !this.canvasRef.current) return

    this.ctx.clearRect(
      0,
      0,
      this.canvasRef.current.width,
      this.canvasRef.current.height,
    )
    this.strokeHistory = []
    this.currentStroke = []
    this.setHasSignature(false)
    this.props.onSignatureChange(null)
  }

  public getDataUrl = (): string | null => {
    if (!this.hasSignature || !this.canvasRef.current) return null
    return this.canvasRef.current.toDataURL('image/png')
  }

  @action.bound
  public loadFromDataUrl(dataUrl: string) {
    if (!dataUrl || !this.canvasRef.current || !this.ctx) return

    const img = new Image()
    img.onload = () => {
      if (!this.ctx || !this.canvasRef.current) return

      this.ctx.clearRect(
        0,
        0,
        this.canvasRef.current.width,
        this.canvasRef.current.height,
      )
      this.ctx.drawImage(
        img,
        0,
        0,
        this.canvasRef.current.width,
        this.canvasRef.current.height,
      )
      this.setHasSignature(true)
      this.props.onSignatureChange(dataUrl)
    }
    img.src = dataUrl
  }

  private initCanvas() {
    const canvas = this.canvasRef.current
    if (!canvas) return

    this.ctx = canvas.getContext('2d')
    if (!this.ctx) return

    this.ctx.strokeStyle = '#000000'
    this.ctx.lineWidth = 2
    this.ctx.lineCap = 'round'
    this.ctx.lineJoin = 'round'

    this.redrawStrokes()
  }

  private getPointerPosition(
    event: React.MouseEvent | React.TouchEvent,
  ): IPoint {
    const canvas = this.canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    if ('touches' in event) {
      const touch = event.touches[0]
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      }
    }

    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY,
    }
  }

  @action.bound
  private startDrawing(event: React.MouseEvent<HTMLCanvasElement>) {
    event.preventDefault()
    this.isDrawing = true
    this.currentStroke = []

    const point = this.getPointerPosition(event)
    this.currentStroke.push(point)

    if (this.ctx) {
      this.ctx.beginPath()
      this.ctx.moveTo(point.x, point.y)
    }
  }

  @action.bound
  private draw(event: React.MouseEvent<HTMLCanvasElement>) {
    if (!this.isDrawing || !this.ctx) return

    const point = this.getPointerPosition(event)
    this.currentStroke.push(point)

    this.ctx.lineTo(point.x, point.y)
    this.ctx.stroke()
  }

  @action.bound
  private stopDrawing() {
    if (!this.isDrawing) return

    this.isDrawing = false

    if (this.currentStroke.length > 0) {
      this.strokeHistory.push([...this.currentStroke])
      this.currentStroke = []
      this.setHasSignature(true)
      this.props.onSignatureChange(this.getDataUrl())
    }
  }

  @action.bound
  private handleTouchStart(event: React.TouchEvent<HTMLCanvasElement>) {
    event.preventDefault()
    this.isDrawing = true
    this.currentStroke = []

    const point = this.getPointerPosition(event)
    this.currentStroke.push(point)

    if (this.ctx) {
      this.ctx.beginPath()
      this.ctx.moveTo(point.x, point.y)
    }
  }

  @action.bound
  private handleTouchMove(event: React.TouchEvent<HTMLCanvasElement>) {
    if (!this.isDrawing || !this.ctx) return

    event.preventDefault()
    const point = this.getPointerPosition(event)
    this.currentStroke.push(point)

    this.ctx.lineTo(point.x, point.y)
    this.ctx.stroke()
  }

  @action
  private setHasSignature(value: boolean) {
    this.hasSignature = value
  }

  private redrawStrokes() {
    if (!this.ctx || !this.canvasRef.current) return

    this.ctx.clearRect(
      0,
      0,
      this.canvasRef.current.width,
      this.canvasRef.current.height,
    )

    for (const stroke of this.strokeHistory) {
      if (stroke.length === 0) continue

      this.ctx.beginPath()
      this.ctx.moveTo(stroke[0].x, stroke[0].y)

      for (let i = 1; i < stroke.length; i++) {
        this.ctx.lineTo(stroke[i].x, stroke[i].y)
      }

      this.ctx.stroke()
    }
  }
}
